import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { ApolloError, useMutation, useQuery } from '@apollo/client'
import PrivacySettingsUI from '@interflora/ui-components/build/components/PrivacySettings/PrivacySettings'
import { HandleSubmit } from '@interflora/ui-components/build/common/props'
import {
  PRIVACY_HUB_FETCH_PREFERENCES,
  PRIVACY_REQUEST_VERIFICATION_EMAIL,
  PRIVACY_HUB_SAVE_PREFERENCES,
} from 'graphql/queries'

const PrivacySettings = () => {
  const [formType, setFormType] = useState<'primary' | 'emailSent'>('primary')
  const [systemError, setIsSystemError] = useState<boolean>(false)
  const [emailAddress, setEmailAddress] = useState<string>('')
  const router = useRouter()

  const [privacyRequestVerificationEmail] = useMutation(PRIVACY_REQUEST_VERIFICATION_EMAIL, {
    onError: (error: ApolloError) => error && setIsSystemError(true),
    onCompleted: () => setIsSystemError(false),
  })

  const [privacyHubSavePreferences] = useMutation(PRIVACY_HUB_SAVE_PREFERENCES, {
    onError: (error: ApolloError) => error && setIsSystemError(true),
    onCompleted: () => setIsSystemError(false),
  })

  // Check for reference and reset strings
  const isValidEmail = useQuery(PRIVACY_HUB_FETCH_PREFERENCES, {
    variables: {
      input: {
        reference: router.query.reference,
        resetString: router.query.resetString,
      },
    },
    skip: !router.query.reference && !router.query.resetString,
  })

  const handleSendEmail = async (values: any): Promise<any> => {
    try {
      await privacyRequestVerificationEmail({ variables: { input: values } })
    } catch (error) {
      setIsSystemError(true)
    }
  }

  const handleSaveSettings = async (values: any): Promise<any> => {
    try {
      await privacyHubSavePreferences({
        variables: {
          input: {
            email: values.email,
            post: values.post,
            digitalMarketing: values.digitalMarketing,
            sms: values.sms,
            reference: router.query.reference,
            resetString: router.query.resetString,
          },
        },
      })
    } catch (error) {
      setIsSystemError(true)
    }
  }

  // Form submissions
  const onEmailSubmit: HandleSubmit<any> = async (values: any) => {
    await handleSendEmail(values)
  }

  const saveSettings: HandleSubmit<any> = async (values: any) => {
    await handleSaveSettings(values)
  }

  // On page load, check if the user has come from the email
  useEffect(() => {
    if (router.query.reference && router.query.resetString) {
      isValidEmail.data ? setFormType('emailSent') : setFormType('primary')
      setEmailAddress(isValidEmail.data?.privacyHubFetchPreferences.emailAddress)
    }
  }, [isValidEmail.data, router.query.reference, router.query.resetString])

  return isValidEmail.loading ? (
    <h1>Loading...</h1>
  ) : (
    <>
      <PrivacySettingsUI
        formType={formType}
        onSubmit={onEmailSubmit}
        saveSettings={saveSettings}
        emailAddress={emailAddress}
        systemError={systemError}
        values={isValidEmail.data?.privacyHubFetchPreferences?.preferences}
      />
    </>
  )
}

export default PrivacySettings
